import createAuth0Client from '@auth0/auth0-spa-js';
import axios from 'axios';
import store from '../store';
import * as AuthReducer from '../reducers/authReducer';
import { setError } from '../reducers/uiReducer';
import StorageService from './StorageService';

const INTERACTIVE_AUTH_ERRORS = ['mfa_required', 'login_required', 'interaction_required', 'consent_required'];

class AuthService {
  auth0 = null;
  isInitialized = false;
  history = null;
  cachedAccessToken = null; // Cache current token to avoid unnecessary localStorage writes
  isRedirectingForAuth = false;

  setHistory = (history) => {
    this.history = history;
  }

  init = async () => {
    if (this.isInitialized) return;

    this.auth0 = await createAuth0Client({
      domain: process.env.REACT_APP_AUTH0_DOMAIN,
      client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      redirect_uri: `${window.location.origin}/callback`,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
    });
    this.isInitialized = true;

    if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
      try {
        await this.auth0.handleRedirectCallback();

        const accessToken = await this.auth0.getTokenSilently();
        const idTokenClaims = await this.auth0.getIdTokenClaims();
        const idToken = idTokenClaims?.__raw;

        if (accessToken) {
          this.updateAccessToken(accessToken);
        }
        if (idToken) {
          this.updateIdToken(idToken);
        }

        StorageService.createEntry('authType', 'auth0');

        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        const path = await store.dispatch(AuthReducer.loggedIn());

        if (this.history) {
          this.history.push(path || '/');
        } else {
          window.location.href = path || '/';
        }
      } catch (err) {
        console.error('Error handling redirect callback:', err);
        store.dispatch(setError(err));

        sessionStorage.clear();
        this.clearTokens();

        window.location.href = '/auth?logout_message=auth0error';
      }
    } else if (window.location.search.includes('error=')) {
      const params = new URLSearchParams(window.location.search);
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      window.history.replaceState({}, document.title, window.location.pathname);

      let logoutMessage = 'auth0error';
      if (error === 'access_denied') {
        logoutMessage = 'access_denied';
      } else if (error === 'unauthorized') {
        logoutMessage = 'user_blocked';
      } else if (errorDescription?.toLowerCase().includes('blocked')) {
        logoutMessage = 'user_blocked';
      } else if (error === 'login_required') {
        logoutMessage = 'login_required';
      } else if (error === 'consent_required') {
        logoutMessage = 'consent_required';
      }

      this.clearTokens();
      sessionStorage.clear();
      window.location.replace(`/login?logout_message=${logoutMessage}`);
    } else {
      // Load cached token on init
      const token = localStorage.getItem('access_token');
      if (token) {
        this.cachedAccessToken = token;
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      }
    }
  }

  login = async (errorMessage) => {
    await this.init();

    if (!this.auth0) throw new Error('Auth0 client not initialized');

    return this.auth0.loginWithRedirect({
      redirect_uri: `${window.location.origin}/callback`,
      scope: 'openid profile email offline_access',
      ...(errorMessage && { logout_message: errorMessage }),
    });
  }

  handleAuthentication = async (props) => {
    await this.init();
    try {
      const accessToken = await this.getAccessToken();
      const idToken = (await this.auth0.getIdTokenClaims())?.__raw;
      if (accessToken) {
        this.updateAccessToken(accessToken);
        if (idToken) {
          this.updateIdToken(idToken);
        }
        StorageService.createEntry('authType', 'auth0');
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        const path = await store.dispatch(AuthReducer.loggedIn());
        if (path) props.history.push(path);
      } else {
        this.login();
      }
    } catch (err) {
      store.dispatch(setError(err));
      this.login();
    }
  }

  getAccessToken = async () => {
    await this.init();
    try {
      // Auth0 SDK automatically handles:
      // 1. Checking if token is expired/expiring
      // 2. Using refresh token to get new access token
      // 3. Caching tokens appropriately
      const token = await this.auth0.getTokenSilently();

      // Only update if token changed
      if (token !== this.cachedAccessToken) {
        this.updateAccessToken(token);
      }
      const idTokenClaims = await this.auth0.getIdTokenClaims();
      const idToken = idTokenClaims?.__raw;
      if (idToken) {
        this.updateIdToken(idToken);
      }

      return token;
    } catch (err) {
      if (INTERACTIVE_AUTH_ERRORS.includes(err.error) && !this.isRedirectingForAuth) {
        this.isRedirectingForAuth = true;
        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (!currentPath.includes('/callback') && !currentPath.includes('/auth') && !currentPath.includes('/login')) {
          sessionStorage.setItem('redirect_url', currentPath);
        }

        this.auth0.loginWithRedirect({
          redirect_uri: `${window.location.origin}/callback`,
          scope: 'openid profile email offline_access',
        });
        return null;
      }
      console.error('Error getting access token:', err);
      throw err;
    }
  }

  getIdToken = async () => {
    await this.init();
    return (await this.auth0.getIdTokenClaims())?.__raw;
  }

  /**
   * Update access token in localStorage only if it has changed
   * @param {string} newToken - The new access token
   */
  updateAccessToken(newToken) {
    if (newToken && newToken !== this.cachedAccessToken) {
      localStorage.setItem('access_token', newToken);
      this.cachedAccessToken = newToken;
      axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      return true;
    }
    return false;
  }

  /**
   * Update ID token in localStorage only if it has changed
   * @param {string} newToken - The new ID token
   */
  updateIdToken(newToken) {
    const currentIdToken = localStorage.getItem('id_token');
    if (newToken && newToken !== currentIdToken) {
      localStorage.setItem('id_token', newToken);
      return true;
    }
    return false;
  }

  /**
   * Clear all tokens from cache and storage
   */
  clearTokens() {
    this.cachedAccessToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('authType');
    delete axios.defaults.headers.common.Authorization;
  }

  setSession = () => {
    // no-op: tokens are managed by the SDK
  }

  logout = async (logoutType) => {
    try {
      await this.init();

      this.clearTokens();

      let params = '';
      if (logoutType) params = `?logout_message=${logoutType}`;

      if (!this.auth0) {
        console.error('Auth0 client not initialized, redirecting manually');
        window.location.href = `${window.location.origin}/auth${params}`;
        return;
      }

      await this.auth0.logout({
        returnTo: `${window.location.origin}/auth${params}`,
      });
    } catch (err) {
      console.error('Logout error:', err);
      this.clearTokens();
      sessionStorage.clear();

      let params = '';
      if (logoutType) params = `?logout_message=${logoutType}`;
      window.location.href = `${window.location.origin}/auth${params}`;
    }
  }
}

export default new AuthService();
